import { Box, type BoxProps } from "@chakra-ui/react";
import { forwardRef, ReactNode } from "react";

export interface InfoBoxProps extends BoxProps {
  /**
   * Visual style variant for the info box
   * @default 'info'
   */
  variant?: "info" | "success" | "warning" | "error";
  children: ReactNode;
}

/**
 * InfoBox - A themed container for displaying informational content with visual emphasis.
 * Provides consistent styling across the application with automatic theme support.
 *
 * @example
 * ```tsx
 * <InfoBox variant="info">
 *   <Text>This is an informational message</Text>
 * </InfoBox>
 * ```
 */
export const InfoBox = forwardRef<HTMLDivElement, InfoBoxProps>(
  function InfoBox({ variant = "info", children, ...props }, ref) {
    // Soft-editorial accent palette — pastel wash + deeper rule + readable fg.
    // Tokens flow from theme/accents.ts through CSS vars in theme/system.ts.
    // Each variant maps to one semantic accent role.
    const variantStyles: Record<
      NonNullable<InfoBoxProps["variant"]>,
      BoxProps
    > = {
      info: {
        bg: "var(--accent-info-wash)",
        borderColor: "var(--accent-info-rule)",
        color: "var(--accent-info-fg)",
      },
      success: {
        bg: "var(--accent-healthy-wash)",
        borderColor: "var(--accent-healthy-rule)",
        color: "var(--accent-healthy-fg)",
      },
      warning: {
        bg: "var(--accent-warning-wash)",
        borderColor: "var(--accent-warning-rule)",
        color: "var(--accent-warning-fg)",
      },
      error: {
        bg: "var(--accent-risk-wash)",
        borderColor: "var(--accent-risk-rule)",
        color: "var(--accent-risk-fg)",
      },
    };

    return (
      <Box
        ref={ref}
        p={3}
        borderRadius="var(--radius-tile)"
        borderLeft="3px solid"
        {...variantStyles[variant]}
        {...props}
      >
        {children}
      </Box>
    );
  },
);
